import { useEffect, useMemo, useRef, useState } from 'react'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../store'
import { authService } from '../services/auth'
import { tenantService } from '../services/tenant'
import BrandLogo from './BrandLogo'
import { useModalFocus } from './ui/focus'

const navigationItems = [
  { label: 'Dashboard', shortLabel: 'Db', to: '/' },
  { label: 'Companies', shortLabel: 'Co', to: '/companies' },
  { label: 'Contacts', shortLabel: 'Ct', to: '/contacts' },
  { label: 'Pipeline', shortLabel: 'Pi', to: '/pipeline' },
  { label: 'Products', shortLabel: 'Pr', to: '/products' },
  { label: 'Quotes', shortLabel: 'Qt', to: '/quotes' },
  { label: 'Contracts', shortLabel: 'Cn', to: '/contracts' },
  { label: 'Subscriptions', shortLabel: 'Su', to: '/subscriptions' },
  { label: 'Tasks', shortLabel: 'Tk', to: '/tasks' },
  { label: 'Invoicing', shortLabel: 'Iv', to: '/billing' },
]

const adminNavigationItems = [
  { label: 'Settings', to: '/settings' },
  { label: 'Company & Workspace', to: '/company' },
]

const pageTitles: Record<string, string> = {
  '/': 'Dashboard',
  '/companies': 'Companies',
  '/contacts': 'Contacts',
  '/pipeline': 'Sales Pipeline',
  '/products': 'Products & Services',
  '/quotes': 'Quotes',
  '/contracts': 'Contracts',
  '/subscriptions': 'Subscriptions',
  '/tasks': 'Tasks',
  '/billing': 'Invoicing',
  '/settings': 'Settings',
  '/company': 'Company & Workspace',
  '/admin': 'Platform Admin',
}

const MainLayout = () => {
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const setUser = useAuthStore((state) => state.setUser)
  const navigate = useNavigate()
  const location = useLocation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isNavigationOpen, setIsNavigationOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const navigationDialogRef = useRef<HTMLDivElement | null>(null)
  const navigationCloseButtonRef = useRef<HTMLButtonElement | null>(null)

  const { data: profileResponse } = useQuery({
    queryKey: ['auth', 'profile'],
    queryFn: () => authService.getProfile().then((response) => response.data),
    staleTime: 60_000,
    retry: false,
  })

  const { data: tenantProfileResponse } = useQuery({
    queryKey: ['tenant', 'profile'],
    queryFn: tenantService.getProfile,
    staleTime: 60_000,
  })

  useEffect(() => {
    const profileUser = profileResponse?.data.user
    if (profileUser) {
      setUser(profileUser)
    }
  }, [profileResponse, setUser])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
    }

    window.addEventListener('mousedown', handleClickOutside)
    return () => window.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useModalFocus({
    isOpen: isNavigationOpen,
    containerRef: navigationDialogRef,
    initialFocusRef: navigationCloseButtonRef,
    onClose: () => setIsNavigationOpen(false),
  })

  const tenantProfile = tenantProfileResponse?.data.profile
  const currentTitle = useMemo(() => pageTitles[location.pathname] || 'Workspace', [location.pathname])
  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'Workspace user'
  const workspaceName = tenantProfile?.workspaceName || 'PureIT CRM'
  const companyName = tenantProfile?.companyName || workspaceName
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('') || 'PI'

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleMenuNavigation = (path: string) => {
    setIsMenuOpen(false)
    setIsNavigationOpen(false)
    navigate(path)
  }

  const adminItems = user?.isPlatformAdmin
    ? [...adminNavigationItems, { label: 'Platform Admin', to: '/admin' }]
    : adminNavigationItems

  const renderNavLink = (item: { label: string; shortLabel?: string; to: string }, isMobile = false) => (
    <NavLink
      key={item.to}
      to={item.to}
      onClick={() => {
        if (isMobile) {
          setIsNavigationOpen(false)
        }
      }}
      className={({ isActive }) =>
        isMobile
          ? `flex min-h-12 items-center rounded-2xl px-4 text-sm font-medium ${
              isActive ? 'bg-slate-950 text-white' : 'text-slate-700 hover:bg-slate-100'
            }`
          : `group flex items-center justify-center rounded-2xl px-3 py-3 text-sm font-medium transition lg:justify-start lg:gap-3 lg:px-4 ${
              isActive
                ? 'bg-[#1976F3] text-white shadow-[0_10px_24px_rgba(25,118,243,0.22)]'
                : 'text-slate-300 hover:bg-white/8 hover:text-white'
            }`
      }
    >
      {!isMobile && item.shortLabel && (
        <span className='flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-xs font-semibold text-white lg:hidden'>
          {item.shortLabel}
        </span>
      )}
      <span className={isMobile ? '' : 'hidden lg:inline'}>{item.label}</span>
    </NavLink>
  )

  return (
    <div className='flex min-h-screen overflow-hidden bg-transparent text-slate-900'>
      <aside className='hidden w-80 shrink-0 flex-col border-r border-white/10 bg-slate-950 text-white shadow-[14px_0_28px_rgba(2,6,23,0.18)] lg:flex'>
        <div className='border-b border-white/10 px-4 py-6 lg:px-6 lg:py-8'>
          <div className='flex items-center justify-center rounded-2xl border border-white/10 bg-white/5 px-3 py-4 lg:justify-start lg:px-4'>
            <BrandLogo variant='light' className='h-8 w-auto lg:h-10' />
          </div>
          <p className='mt-4 hidden text-sm leading-6 text-slate-300 lg:block'>
            Sales, delivery, subscriptions, and invoicing aligned under one clean commercial workspace.
          </p>
        </div>

        <nav className='flex-1 space-y-2 px-3 py-5 lg:px-4'>
          {navigationItems.map((item) => renderNavLink(item))}
          <div className='pt-4'>
            <div className='px-4 pb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500'>
              Administration
            </div>
            <div className='space-y-2'>{adminItems.map((item) => renderNavLink(item))}</div>
          </div>
        </nav>

        <div className='border-t border-white/10 px-3 py-4 lg:px-4'>
          <div className='rounded-2xl border border-white/10 bg-white/5 px-4 py-4'>
            <div className='text-[10px] font-semibold uppercase tracking-[0.28em] text-sky-200'>Workspace</div>
            <div className='mt-2 truncate text-sm font-medium text-white lg:text-base'>{workspaceName}</div>
            <div className='mt-1 hidden text-xs text-slate-400 lg:block'>Tenant-aware CRM platform</div>
          </div>
        </div>
      </aside>

      <div className='flex min-h-screen min-w-0 flex-1 flex-col'>
        <header className='sticky top-0 z-20 border-b border-slate-200/70 bg-[rgba(255,255,255,0.88)] backdrop-blur-xl'>
          <div className='flex items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8'>
            <div className='flex min-w-0 items-center gap-3'>
              <button
                type='button'
                onClick={() => setIsNavigationOpen(true)}
                className='inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm lg:hidden'
                aria-label='Open navigation'
              >
                <Bars3Icon className='h-5 w-5' aria-hidden='true' />
              </button>
              <div className='min-w-0'>
              <div className='text-xs font-semibold uppercase tracking-[0.18em] text-[#1976F3]'>PureIT Workspace</div>
              <h2 className='mt-2 truncate text-2xl font-semibold text-slate-950 sm:text-3xl'>{currentTitle}</h2>
              <p className='mt-1 truncate text-sm text-slate-500'>{workspaceName}</p>
              </div>
            </div>

            <div className='flex items-center gap-3'>
              <div className='hidden rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-right shadow-sm sm:block'>
                <div className='text-sm font-medium text-slate-900'>{companyName}</div>
                <div className='text-xs text-slate-500'>{displayName}</div>
              </div>

              <div ref={menuRef} className='relative'>
                <button
                  type='button'
                  onClick={() => setIsMenuOpen((current) => !current)}
                  className='flex items-center gap-3 rounded-full border border-slate-200 bg-white px-3 py-2 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md'
                >
                  <span className='flex h-11 w-11 items-center justify-center rounded-full bg-[#1976F3] text-sm font-semibold text-white shadow-[0_10px_24px_rgba(25,118,243,0.28)]'>
                    {initials}
                  </span>
                  <span className='hidden text-sm sm:block'>
                    <span className='block font-medium text-slate-900'>{displayName}</span>
                    <span className='block text-xs text-slate-500'>
                      {user?.role === 'admin' ? 'Tenant admin' : user?.role || 'Team member'}
                    </span>
                  </span>
                </button>

                {isMenuOpen && (
                  <div className='absolute right-0 top-full mt-3 w-80 rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_12px_28px_rgba(15,23,42,0.14)]'>
                    <div className='rounded-2xl border border-slate-200 bg-slate-50 p-4'>
                      <div className='text-sm font-semibold text-slate-900'>{displayName}</div>
                      <div className='mt-1 text-xs text-slate-500'>{user?.email}</div>
                      <div className='mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1976F3]'>
                        {companyName}
                      </div>
                    </div>

                    <div className='mt-3 space-y-1'>
                      <button
                        type='button'
                        onClick={() => handleMenuNavigation('/company')}
                        className='w-full rounded-2xl px-4 py-3 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-100'
                      >
                        Company & Workspace
                      </button>
                      <button
                        type='button'
                        onClick={() => handleMenuNavigation('/settings')}
                        className='w-full rounded-2xl px-4 py-3 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-100'
                      >
                        Settings
                      </button>
                      {user?.isPlatformAdmin && (
                        <button
                          type='button'
                          onClick={() => handleMenuNavigation('/admin')}
                          className='w-full rounded-2xl px-4 py-3 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-100'
                        >
                          Platform Admin
                        </button>
                      )}
                      <button
                        type='button'
                        onClick={handleLogout}
                        className='w-full rounded-2xl px-4 py-3 text-left text-sm font-medium text-rose-600 transition hover:bg-rose-50'
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className='min-w-0 flex-1 overflow-y-auto overflow-x-hidden'>
          <Outlet />
        </main>
      </div>

      {isNavigationOpen && (
        <div className='fixed inset-0 z-40 lg:hidden'>
          <div
            className='absolute inset-0 bg-slate-950/45'
            onClick={() => setIsNavigationOpen(false)}
            aria-hidden='true'
          />
          <div
            ref={navigationDialogRef}
            tabIndex={-1}
            className='absolute inset-y-0 left-0 flex w-[340px] max-w-[calc(100vw_-_32px)] flex-col bg-white shadow-2xl outline-none'
            role='dialog'
            aria-modal='true'
            aria-labelledby='workspace-navigation-title'
          >
            <div className='flex items-center justify-between border-b border-slate-200 px-5 py-4'>
              <BrandLogo className='h-8 w-auto' />
              <button
                ref={navigationCloseButtonRef}
                type='button'
                onClick={() => setIsNavigationOpen(false)}
                className='inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 text-slate-700'
                aria-label='Close navigation'
              >
                <XMarkIcon className='h-5 w-5' aria-hidden='true' />
              </button>
            </div>
            <h2 id='workspace-navigation-title' className='sr-only'>Workspace navigation</h2>
            <nav className='min-h-0 flex-1 overflow-y-auto px-4 py-5'>
              <div className='space-y-1'>{navigationItems.map((item) => renderNavLink(item, true))}</div>
              <div className='mt-6 border-t border-slate-200 pt-5'>
                <div className='px-4 pb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400'>
                  Administration
                </div>
                <div className='space-y-1'>{adminItems.map((item) => renderNavLink(item, true))}</div>
              </div>
            </nav>
          </div>
        </div>
      )}
    </div>
  )
}

export default MainLayout
